import axios from 'axios';

/** 에러 본문에서 읽는 필드만 — 서버 응답 본문·AxiosResponse 를 그대로 reject 한 경우 둘 다 이 모양으로 읽는다(값은 런타임에 typeof 로 확인). */
type ErrorBody = {
  code?: string;
  message?: string;
  error?: { code?: string; message?: string };
  result?: { code?: string; message?: string };
};
/** axios 가 아닌 에러 객체(responseErrorHandler 가 reject 한 AxiosResponse 등)에서 읽는 모양. */
type ErrorLike = { status?: unknown; response?: { status?: unknown; data?: ErrorBody }; data?: ErrorBody };

// TRAD-2107 — "네트워크/서버 실패"와 "그 리소스가 실제로 존재하지 않음(404)"을 가른다.
// 여러 상세 화면(JournalDetailPage/JournalComposerPage edit/PolymarketDetailPage)이
// `isError || !entry` 하나로 뭉뚱그려 **재시도해도 될 상황을 "찾을 수 없음"으로 오표시**했다
// (ErrorState 주석의 TRAD-2143 원칙과 동일 — 실패를 사실 진술로 바꿔 말하면 안 된다).
// 404만 "진짜로 없음"(재시도 무의미)으로 보고, 그 외(5xx/타임아웃/네트워크 끊김)는 재시도
// 가능한 실패로 분리해야 한다.
export const extractErrorStatus = (error: unknown): number | null => {
  if (axios.isAxiosError(error)) {
    return error.response?.status ?? null;
  }
  if (error && typeof error === 'object') {
    const err = error as ErrorLike;
    if (typeof err.response?.status === 'number') return err.response.status;
    if (typeof err.status === 'number') return err.status;
  }
  return null;
};

export const extractErrorCode = (error: unknown): string | null => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data;
    if (data?.code) return data.code;
    if (data?.error?.code) return data.error.code;
    if (data?.result?.code) return data.result.code;
  }
  if (error && typeof error === 'object') {
    const err = error as ErrorLike;
    if (err.response?.data?.code) return err.response.data.code;
    if (err.response?.data?.error?.code) return err.response.data.error.code;
    if (err.data?.code) return err.data.code;
    if (err.data?.error?.code) return err.data.error.code;
  }
  return null;
};

export const extractErrorMessage = (error: unknown, fallback: string): string => {
  // AxiosError: error.response.data.message 또는 error.response.data.result.message
  if (axios.isAxiosError(error)) {
    const data = error.response?.data;
    if (data?.message) return data.message;
    if (data?.error?.message) return data.error.message;
    if (data?.result?.message) return data.result.message;
  }
  // responseErrorHandler에서 reject(response)로 AxiosResponse 객체가 직접 전달되는 경우
  if (error && typeof error === 'object') {
    const err = error as ErrorLike;
    // { response: { data: { message } } } 구조
    if (err.response?.data?.message) return err.response.data.message;
    if (err.response?.data?.error?.message) return err.response.data.error.message;
    if (err.response?.data?.result?.message) return err.response.data.result.message;
    // { data: { message } } 구조
    if (err.data?.message) return err.data.message;
    if (err.data?.error?.message) return err.data.error.message;
    if (err.data?.result?.message) return err.data.result.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return fallback;
};
